import Newsletter from "../services/Newsletter";
import assign from "lodash.assign";
import { LOCATION_CHANGE } from "react-router-redux";
import { formatNewsletters } from "../utils/newsletters";
import ERROR_MESSAGES from "common/constants/ErrorMessages";

export const SUCCESS_MESSAGE_TYPE = "success";

const ERROR_MESSAGE_TYPE = "error";
const DATA_SAVED = "Your newsletter preferences have been saved.";
const UNSUBSCRIBED = "You have been unsubscribed from all Bloomberg newsletters.";

// Actions (must be name-spaced for reducer)

export const NEWSLETTER_CLEAR_MESSAGE = "NEWSLETTER_CLEAR_MESSAGE";
export const NEWSLETTER_SHOW_NEWSLETTER_MESSAGE = "NEWSLETTER_SHOW_NEWSLETTER_MESSAGE";
export const NEWSLETTER_UPDATE_NEWSLETTER_SUBSCRIPTION = "NEWSLETTER_UPDATE_NEWSLETTER_SUBSCRIPTION";
export const NEWSLETTER_SUBSCRIBE_ALL = "newsletters/NEWSLETTER_SUBSCRIBE_ALL";
export const NEWSLETTER_UNSUBSCRIBE_ALL = "newsletters/NEWSLETTER_UNSUBSCRIBE_ALL";
export const NEWSLETTERS_LOADING = "newsletters/LOADING";
export const NEWSLETTERS_LOADED = "newsletters/LOADED";
export const SINGLE_NEWSLETTER_LOADED = "newsletters/SINGLE_LOADED";
export const NEWSLETTERS_ERROR = "newsletters/ERROR";
export const NEWSLETTERS_EMAIL_HASH_VALIDATED = "newsletters/EMAIL_HASH_VALIDATED";
export const NEWSLETTERS_EMAIL_HASH_NOT_VALIDATED = "newsletters/NEWSLETTERS_EMAIL_HASH_NOT_VALIDATED";

// Reducer

export function newsletterReducer(state = {}, action = {}) {
    switch (action.type) {
        case LOCATION_CHANGE:
            return {};
        case NEWSLETTER_CLEAR_MESSAGE:
            return assign({}, state, {
                newsletterMessage: null,
                newsletterMessageType: null
            });
        case NEWSLETTER_SHOW_NEWSLETTER_MESSAGE:
            return assign({}, state, {
                newsletterMessage: action.message,
                newsletterMessageType: action.messageType
            });
        case NEWSLETTER_UPDATE_NEWSLETTER_SUBSCRIPTION:
            return assign({}, state, {
                subscribedNewsletterIds: assign({}, state.subscribedNewsletterIds, action.subscription)
            });
        case NEWSLETTERS_LOADED:
            return assign({}, state, {
                dailyNewsletters: action.payload.daily,
                weeklyNewsletters: action.payload.weekly,
                userNewsletters: action.payload.user,
                isSubscriber: action.payload.isSubscriber
            });
        case SINGLE_NEWSLETTER_LOADED:
            return assign({}, state, {
                newsletter: action.payload
            });
        case NEWSLETTERS_LOADING:
            return assign({}, state, {
                newsletters: formatNewsletters(state.newsletters)
            });
        case NEWSLETTERS_ERROR:
            return assign({}, state, {
                error: action.error,
                newsletters: formatNewsletters(state.newsletters)
            });
        case NEWSLETTERS_EMAIL_HASH_VALIDATED:
            return assign({}, state, {
                emailHashValidated: true
            });
        case NEWSLETTERS_EMAIL_HASH_NOT_VALIDATED:
            return assign({}, state, {
                emailHashValidated: false
            });
        default:
            return state;
    }
}

// Action Creators

export function clearMessage() {
    return {
        type: NEWSLETTER_CLEAR_MESSAGE
    };
}

export function showNewsletterMessage(messageType, message) {
    return {
        type: NEWSLETTER_SHOW_NEWSLETTER_MESSAGE,
        messageType,
        message
    };
}

export function updateNewsletterSubscription(subscription) {
    return {
        type: NEWSLETTER_UPDATE_NEWSLETTER_SUBSCRIPTION,
        subscription
    };
}

export function emailHashValidated() {
    return {
        type: NEWSLETTERS_EMAIL_HASH_VALIDATED
    };
}

export function emailHashNotValidated() {
    return {
        type: NEWSLETTERS_EMAIL_HASH_NOT_VALIDATED
    };
}

export function startFetchingNewsletterData(email, hash) {
    return (dispatch) => {
        dispatch({
            type: NEWSLETTERS_LOADING
        });

        return Newsletter.fetchNewsletterData(email, hash)
            .then((newsletterData) => {
                dispatch({
                    type: NEWSLETTERS_LOADED,
                    payload: newsletterData
                });
            })
            .catch((error) => {
                dispatch({
                    type: NEWSLETTERS_ERROR,
                    error: error.errorMessage
                });

                throw error;
            });
    };
}

export function startFetchingSingleNewsletterData(newsletterListId, email, hash) {
    return (dispatch) => {
        dispatch({
            type: NEWSLETTERS_LOADING
        });

        return Newsletter.fetchSingleNewsletterData(newsletterListId, email, hash)
            .then((newsletterData) => {
                dispatch({
                    type: SINGLE_NEWSLETTER_LOADED,
                    payload: newsletterData
                });
            });
    };
}

export function startUpdatingNewsletterSubscription(subscription, email, options) {
    return (dispatch, getState) => {
        const {
            country,
            marketing: {
                isOptedIn
            }
        } = getState();

        const optIn = isOptedIn || country === "US";

        dispatch(clearMessage());
        dispatch(updateNewsletterSubscription(subscription));

        return Newsletter.updateSubscription(subscription, email, { ...options, optIn })
            .then(() => {
                dispatch(showNewsletterMessage(SUCCESS_MESSAGE_TYPE, DATA_SAVED));
            })
            .catch((error) => {
                dispatch({
                    type: NEWSLETTER_SHOW_NEWSLETTER_MESSAGE,
                    messageType: ERROR_MESSAGE_TYPE,
                    message: `${ ERROR_MESSAGES.ERROR_UPDATING_SUBSCRIPTION }. Please try again.`
                });
                dispatch({
                    type: NEWSLETTERS_ERROR,
                    error: `${ ERROR_MESSAGES.ERROR_UPDATING_SUBSCRIPTION }. Please try again.`
                });

                throw error;
            });
    };
}

export function globalUnsubscribe(subscription, email, hash, source) {
    return (dispatch) => {
        dispatch(clearMessage());
        dispatch(updateNewsletterSubscription(subscription));

        return Newsletter.globalUnsubscribe(subscription, email, hash, source)
            .then(() => {
                dispatch(showNewsletterMessage(SUCCESS_MESSAGE_TYPE, UNSUBSCRIBED));
            })
            .catch((error) => {
                dispatch(showNewsletterMessage(ERROR_MESSAGE_TYPE, error.errorMessage));

                throw error;
            });
    };
}

export function validateEmailHash(email, hash) {
    return (dispatch) => {
        if (!email || !hash) {
            dispatch(emailHashNotValidated());

            return;
        }

        return Newsletter.validateEmailHash(email, hash)
            .then(() => {
                dispatch(emailHashValidated());
            })
            .catch(() => {
                dispatch(emailHashNotValidated());
            });
    };
}

export default {
    newsletterReducer,
    startUpdatingNewsletterSubscription,
    startFetchingNewsletterData,
    startFetchingSingleNewsletterData,
    globalUnsubscribe,
    validateEmailHash
};
